import React, { useCallback, useEffect, useState } from 'react'
import { Card, Space, Button, Tag, Form, InputNumber, Modal, Select } from 'antd'
import Table, { type ColumnsType } from 'antd/es/table'
import { type AxiosError } from 'axios'
import { useAppDispatch } from 'Hooks/ReduxHook'
import { PlusOutlined } from '@ant-design/icons'
import { setLoader } from 'Store/slices/commonSlice'
import Http, { type ApiErrorData } from 'Util/Http'
import { HTTP_METHOD, type IPriceList } from 'Util/Interface'
import { toast } from 'sonner'
import TextArea from 'antd/es/input/TextArea'

const PriceList: React.FC = () => {
  const [tableData, setTableData] = useState<IPriceList[]>([])
  const [carData, setCarData] = useState([])
  const [cityData, setCityData] = useState([])
  const [visible, setVisble] = useState(false)
  const [isUpdated, setIsUpdated] = useState<boolean>(false)
  const [updateId, setUpdateId] = useState<string>('')

  const [form] = Form.useForm()
  const dispatch = useAppDispatch()

  const fetch = useCallback(async () => {
    try {
      dispatch(setLoader(true))
      const response = await Http(`/price`)
      if (response.data?.status === 'success' && response?.data?.code === 200) {
        dispatch(setLoader(false))
        setTableData(response.data?.data)
      } else {
        setTableData([])
        dispatch(setLoader(false))
      }
    } catch (error: any) {
      const err = error as AxiosError<ApiErrorData>
      toast.error(err.response?.data?.message ?? 'Something Went Wrong')
      setTableData([])
      dispatch(setLoader(false))
    }
  }, [dispatch])

  const fetchMaster = useCallback(async () => {
    try {
      dispatch(setLoader(true))
      const response = await Http(`/master`)
      if (response.data?.status === 'success' && response?.data?.code === 200) {
        dispatch(setLoader(false))
        setCarData(response.data?.data?.car)
        setCityData(response.data?.data?.city)
      } else {
        setCityData([])
        setCarData([])
        dispatch(setLoader(false))
      }
    } catch (error: any) {
      const err = error as AxiosError<ApiErrorData>
      toast.error(err.response?.data?.message ?? 'Something Went Wrong')
      setCityData([])
      setCarData([])
      dispatch(setLoader(false))
    }
  }, [dispatch])

  useEffect(() => {
    void fetch()
    void fetchMaster()
  }, [fetch, fetchMaster])

  const handleDelete = async (params: IPriceList) => {
    try {
      const response = await Http(`/price/${String(params.id)}`, {
        method: 'DELETE',
      })
      if (response.data?.status === 'success' && response?.data?.code === 200) {
        toast.success(response?.data?.message)
        void fetch()
      }
    } catch (error) {
      const err = error as AxiosError<ApiErrorData>
      toast.error(err.response?.data?.message ?? 'Something Went Wrong')
    }
  }

  const handleUpsert = async (param: IPriceList) => {
    try {
      const response = await Http({
        url: !isUpdated ? '/price' : `/price/${updateId}`,
        method: !isUpdated ? HTTP_METHOD.POST : HTTP_METHOD.PUT,
        data: param,
      })
      if (response.data?.status === 'success' && response?.data?.code === 200) {
        toast.success(response?.data?.message)
        form.resetFields()
        setVisble(false)
        setIsUpdated(false)
        setUpdateId('')
        void fetch()
      } else {
        toast.error(response?.data?.message ?? 'Something Went Wrong')
      }
    } catch (error) {
      const err = error as AxiosError<ApiErrorData>
      toast.error(err.response?.data?.message ?? 'Something Went Wrong')
    }
  }

  const handleUpdate = (param: IPriceList) => {
    setIsUpdated(true)
    setUpdateId(param.id)
    form.setFieldsValue({
      tripType: param.tripType,
      pickupId: param.pickupId,
      dropId: param.dropId,
      carId: param.carId,
      amount: param.amount,
      distance: param.distance,
      extraAmount: param.extraAmount,
      nightAllowance: param.nightAllowance,
      note: param.note,
      status: param.status,
    })
    setVisble(true)
  }

  const handleCancel = () => {
    setVisble(false)
    setIsUpdated(false)
    setUpdateId('')
    form.resetFields()
  }

  const columns: ColumnsType<IPriceList> = [
    {
      title: 'Sr. No.',
      key: 'serial',
      dataIndex: 'serial',
      render: (_value, _record, index: number) => index + 1,
    },
    {
      title: 'TripType',
      key: 'tripType',
      dataIndex: 'tripType',
    },
    {
      title: 'Amount',
      key: 'amount',
      dataIndex: 'amount',
    },
    {
      title: 'Total Distance',
      key: 'distance',
      dataIndex: 'distance',
    },
    {
      title: 'Price After Distance Per/KM',
      key: 'extraAmount',
      dataIndex: 'extraAmount',
    },
    {
      title: 'Night Allowance',
      key: 'nightAllowance',
      dataIndex: 'nightAllowance',
    },
    {
      title: 'Pickup Location',
      key: 'pickup_data',
      dataIndex: 'pickup_data',
      render: (_, data: any) => <>{data?.pickup?.name}</>,
    },
    {
      title: 'Drop Location',
      key: 'drop_data',
      dataIndex: 'drop_data',
      render: (_, data: any) => <>{data?.drop?.name}</>,
    },
    {
      title: 'Car',
      key: 'car_data',
      dataIndex: 'car_data',
      render: (_, data: any) => <>{data?.car?.name}</>,
    },
    {
      title: 'Notes',
      key: 'note',
      dataIndex: 'note',
    },
    {
      title: 'Active',
      key: 'status',
      dataIndex: 'status',
      render: (value) => <Tag color={value === 'Active' ? 'green' : 'red'}>{value}</Tag>,
    },
    {
      title: 'Action',
      dataIndex: 'actions',
      render: (_text, record: IPriceList) => {
        return (
          <Space>
            <Button
              onClick={() => {
                handleUpdate(record)
              }}
            >
              Update
            </Button>
            <Button
              danger
              onClick={() => {
                void handleDelete(record)
              }}
            >
              Delete
            </Button>
          </Space>
        )
      },
    },
  ]

  return (
    <>
      <div className="content-wrapper">
        <Card
          className="card-wrapper"
          title={
            <div className="row justify-between">
              <div>
                <h3>Price List</h3>
              </div>
              <div>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setVisble(true)
                  }}
                >
                  Add Price List
                </Button>
              </div>
            </div>
          }
          style={{ padding: '1.25rem 1.25rem 0' }}
        >
          <Table columns={columns} dataSource={tableData} rowKey={(dataIndex: IPriceList) => dataIndex?.id} scroll={{ x: 800 }} bordered size="middle" />
        </Card>
      </div>
      <Modal
        title={!isUpdated ? 'Add Price List' : 'Update Price List'}
        open={visible}
        onCancel={handleCancel}
        footer={[
          <Space key={'action'}>
            <Button type="primary" onClick={form.submit} className="btn-padding">
              Save
            </Button>
            <Button danger onClick={handleCancel}>
              Cancel
            </Button>
          </Space>,
        ]}
        width={700}
      >
        <Form
          form={form}
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 13 }}
          autoComplete="off"
          onFinish={(value: IPriceList) => {
            void handleUpsert(value)
          }}
          requiredMark="optional"
        >
          <Form.Item
            label="Trip Type"
            name="tripType"
            rules={[
              {
                required: true,
                message: 'Select Trip Type!',
              },
            ]}
          >
            <Select
              placeholder="Select Trip Type"
              className="custom-select"
              options={[
                { value: 'OneWay', label: 'OneWay' },
                { value: 'RoundWay', label: 'RoundWay' },
              ]}
            />
          </Form.Item>

          <Form.Item
            label="Pickup Location"
            name="pickupId"
            rules={[
              {
                required: true,
                message: 'Select Pickup Location!',
              },
            ]}
          >
            <Select placeholder="Select Pickup Location" className="custom-select">
              {cityData?.map((ele: any, idx) => {
                return (
                  <Select.Option value={ele?.id} key={idx}>
                    {ele?.name}
                  </Select.Option>
                )
              })}
            </Select>
          </Form.Item>

          <Form.Item
            label="Drop Location"
            name="dropId"
            rules={[
              {
                required: true,
                message: 'Select Drop Location!',
              },
            ]}
          >
            <Select placeholder="Select Drop Location" className="custom-select">
              {cityData?.map((ele: any, idx) => {
                return (
                  <Select.Option value={ele?.id} key={idx}>
                    {ele?.name}
                  </Select.Option>
                )
              })}
            </Select>
          </Form.Item>

          <Form.Item
            label="Car"
            name="carId"
            rules={[
              {
                required: true,
                message: 'Select Car!',
              },
            ]}
          >
            <Select placeholder="Select Car" className="custom-select">
              {carData?.map((ele: any, idx) => {
                return (
                  <Select.Option value={ele?.id} key={idx}>
                    {ele?.name}
                  </Select.Option>
                )
              })}
            </Select>
          </Form.Item>

          <Form.Item
            label="Amount"
            name="amount"
            rules={[
              {
                required: true,
                message: 'Please Enter Amount!',
              },
            ]}
          >
            <InputNumber className="custom-input-number" min={0} placeholder="Enter Amount" />
          </Form.Item>

          <Form.Item
            label="Total Distance"
            name="distance"
            rules={[
              {
                required: true,
                message: 'Please Enter Total Distance!',
              },
            ]}
          >
            <InputNumber className="custom-input-number" min={0} placeholder="Enter Total Distance" />
          </Form.Item>

          <Form.Item
            label="Price After Distance Per/KM"
            name="extraAmount"
            rules={[
              {
                required: true,
                message: 'Please Enter Amount!',
              },
            ]}
          >
            <InputNumber className="custom-input-number" min={0} placeholder="Enter Amount" />
          </Form.Item>

          <Form.Item
            label="Night Allowance Price"
            name="nightAllowance"
            rules={[
              {
                required: true,
                message: 'Please Enter Night Allowance!',
              },
            ]}
          >
            <InputNumber className="custom-input-number" min={0} placeholder="Enter Night Allowance" />
          </Form.Item>

          <Form.Item
            label="Notes"
            name="note"
            rules={[
              {
                required: false,
                message: 'Please Enter Notes!',
              },
            ]}
          >
            <TextArea className="custom-input-number" placeholder="Enter Notes" />
          </Form.Item>

          <Form.Item
            label="Status"
            name="status"
            rules={[
              {
                required: true,
                message: 'Select Status!',
              },
            ]}
          >
            <Select
              placeholder="Select Status"
              className="custom-select"
              options={[
                { value: 'Active', label: 'Active' },
                { value: 'InActive', label: 'InActive' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default PriceList
